import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setEmail } from "../../store/authSlice";
import AuthGeneralInput from "../../components/input/authinput";
import { AppButton } from "../../components/buttons/button";
import { H1Title, Paragraph } from "../../components/typography/typography";
import AuthService from "../../services/authService";
import { Error as ToastError } from "../../components/toast/toast";
import _route from "../../constants/routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

const IDEA_BANK_URL = "https://docs.google.com/forms/d/e/1FAIpQLScIPyF9DoE4qQ1x_GTwPOw2logAmoOVqRWb0o6FsWrXsMje8w/viewform?usp=header";

export default function Signup() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '',
    dateOfBirth: '', phone: '',
    eligibilityStatus: 'college_student',
    institution: '', fieldOfStudy: '', graduationYear: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [ageRejected, setAgeRejected] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step === 1) {
      if (!form.firstName || !form.lastName || !form.email || !form.password) {
        ToastError('Please fill in all required fields');
        return;
      }
      if (form.password.length < 8) {
        setErrors({ password: 'Password must be at least 8 characters' });
        return;
      }
      setStep(2);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        dateOfBirth: form.dateOfBirth,
        eligibility: {
          status: form.eligibilityStatus,
          institution: form.institution,
        }
      };
      if (form.phone.trim()) payload.phone = form.phone;
      if (form.fieldOfStudy.trim()) payload.eligibility.fieldOfStudy = form.fieldOfStudy;
      if (form.graduationYear) payload.eligibility.graduationYear = parseInt(form.graduationYear);
      await AuthService.register(payload);
      dispatch(setEmail(form.email));
      navigate(_route._verify);
    } catch (err) {
      const errObj = err.response?.data?.error;
      if (errObj?.code === 'AGE_RESTRICTED' || /age restriction/i.test(errObj?.message || '')) {
        setAgeRejected(true);
      } else {
        ToastError(errObj?.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  if (ageRejected) {
    return (
      <div className="w-full space-y-5 text-center">
        <div className="w-16 h-16 bg-primary-bg rounded-full flex items-center justify-center mx-auto">
          <FontAwesomeIcon icon={faLightbulb} className="text-primary text-2xl" />
        </div>
        <div>
          <H1Title size="sm">Outside the eligibility range</H1Title>
          <Paragraph size="base" className="text-grey-667 mt-2">
            You do not meet the 18-25 age restriction for this challenge, but thank you for your interest.
          </Paragraph>
        </div>
        <div className="bg-primary-bg/40 border border-primary/20 rounded-lg p-4 text-left">
          <Paragraph size="sm" className="text-grey-dark">
            We appreciate your interest and still want to hear your idea. Feel free to submit it to our Idea Bank.
          </Paragraph>
        </div>
        <a href={IDEA_BANK_URL} target="_blank" rel="noopener noreferrer">
          <AppButton type="button">
            <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" /> Submit to Idea Bank
          </AppButton>
        </a>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-sm text-grey-667 hover:text-primary cursor-pointer"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-5">
      <div>
        <H1Title size="sm">
          {step === 1 ? 'Create Your Account' : 'Eligibility Info'}
        </H1Title>
        <Paragraph size="base" className="text-grey-667 mt-1">
          {step === 1 ? 'Join The Human Development Lab' : 'Tell us about your background'}
        </Paragraph>
      </div>

      {step === 1 ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div><AuthGeneralInput label="First Name" name="firstName" placeholder="First name" value={form.firstName} change={handleChange} /></div>
            <div><AuthGeneralInput label="Last Name" name="lastName" placeholder="Last name" value={form.lastName} change={handleChange} /></div>
          </div>
          <AuthGeneralInput label="Email" type="email" name="email" placeholder="Enter your email" value={form.email} change={handleChange} />
          <AuthGeneralInput label="Password" type="password" name="password" placeholder="Min 8 characters" value={form.password} change={handleChange} error={errors.password} />
          <AppButton type="submit">Continue</AppButton>
        </>
      ) : (
        <>
          <AuthGeneralInput label="Date of Birth" type="date" name="dateOfBirth" placeholder="" value={form.dateOfBirth} change={handleChange} />
          <AuthGeneralInput label="Phone (optional)" type="tel" name="phone" placeholder="+1 (555) 000-0000" value={form.phone} change={handleChange} notrequired />

          <div>
            <label className="block text-xs md:text-sm mb-2 text-black">I am a <span className="text-red-500">*</span></label>
            <select
              name="eligibilityStatus"
              value={form.eligibilityStatus}
              onChange={handleChange}
              className="w-full border border-grey-200 bg-white px-4 py-3 text-sm rounded-md outline-none"
            >
              <option value="college_student">College Student</option>
              <option value="early_professional">Early Professional (within 3 years of graduation)</option>
            </select>
          </div>

          <AuthGeneralInput label="Institution / Organization" name="institution" placeholder="e.g. MIT, Google" value={form.institution} change={handleChange} />
          <AuthGeneralInput label="Field of Study (optional)" name="fieldOfStudy" placeholder="e.g. Public Health" value={form.fieldOfStudy} change={handleChange} notrequired />
          <AuthGeneralInput label="Graduation Year (optional)" type="number" name="graduationYear" placeholder="e.g. 2025" value={form.graduationYear} change={handleChange} notrequired />

          <div className="flex gap-3">
            <AppButton type="button" variant="outline" onClick={() => setStep(1)} fullWidth={false} className="px-6">
              Back
            </AppButton>
            <AppButton type="submit" loading={loading}>
              Create Account
            </AppButton>
          </div>
        </>
      )}

      <p className="text-sm text-center text-grey-667">
        Already have an account?{' '}
        <Link to={_route._login} className="text-primary font-semibold hover:underline">
          Sign In
        </Link>
      </p>
    </form>
  );
}
